import { useEffect, useRef, useState } from 'react';
import { animate } from 'framer-motion';

export interface MotionNumberProps {
  value: number;
  /** Tween duration in seconds (defaults to 0.4s). */
  duration?: number;
  /** Optional formatter for the displayed value (defaults to `Math.round`). */
  format?: (n: number) => string;
  className?: string;
}

/**
 * Animate a numeric value smoothly when it changes. Used for the audit
 * toolbar's "displayed N / loaded M" counters and similar at-a-glance metrics.
 * Skips animation on first mount and respects prefers-reduced-motion.
 */
export function MotionNumber({ value, duration = 0.4, format, className }: MotionNumberProps) {
  const [display, setDisplay] = useState(value);
  const prevRef = useRef(value);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      prevRef.current = value;
      setDisplay(value);
      return;
    }
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setDisplay(value);
      prevRef.current = value;
      return;
    }
    const controls = animate(prevRef.current, value, {
      duration,
      ease: 'easeOut',
      onUpdate: (v) => setDisplay(v),
      onComplete: () => {
        prevRef.current = value;
      },
    });
    return () => controls.stop();
  }, [value, duration]);

  const formatted = format ? format(display) : String(Math.round(display));
  return <span className={className}>{formatted}</span>;
}
